/**
 * SLA Settings -- the FI admin's task-queue age bands (GH #2390).
 *
 * Route:  /admin/config/sla
 * Scope:  FI-tenant (admin.aaim.com : 9300, adminPortal), in the FI
 *         Configuration nav group, which is gated on settings:update.
 *
 * The Tasks queue labels each task by its age against two thresholds: under
 * greenHours it reads "Within SLA", from greenHours up to yellowHours
 * "Approaching", and from yellowHours on "Over SLA". This page reads and
 * replaces that pair for the caller's own institution through
 * /api/v1/admin/tenants/me/sla; the request names no tenant.
 *
 * Reading needs settings:read and saving needs settings:update; the server
 * enforces both. A reader without settings:update sees the values and no
 * form.
 *
 * The checks below are the rules TenantSlaConfig states (whole hours from 1
 * to 2147483647, greenHours below yellowHours, an IANA time zone), shown
 * before a save is sent (GH #2390 deliverable 3). The server applies the same
 * rules and stays the authority: a refused save shows its message.
 *
 * An institution that has saved nothing receives greenHours 24, yellowHours
 * 72 and timezone UTC, the values its queue bands against, with
 * `platformDefault` true (getMyTenantSlaConfig). The page shows those values
 * labeled as the platform default the institution has not set, in the form
 * and in the read-only view, until a threshold is saved (Rob's ruling of
 * 2026-10-24 on sla-fi-ownership arc node s7, option (a); GH #4317).
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Alert, BodyText, Button, Card, Heading, Input } from '@aaimplatform/ui';
import { Loader2 } from 'lucide-react';
import type { TenantSlaConfig } from '../../api/services/tenant-sla';
import { useTenantSla, useUpdateTenantSla } from '../../hooks/useTenantSla';
import { useAuthStore } from '../../stores/auth';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';

/** TenantSlaConfig.greenHours / yellowHours: minimum 1, maximum 2147483647. */
const SLA_HOURS_MIN = 1;
const SLA_HOURS_MAX = 2147483647;

const TIME_ZONE_LIST_ID = 'sla-time-zones';

type FormState = { greenHours: string; yellowHours: string; timezone: string };
type FormErrorState = Partial<Record<keyof FormState, string>>;

/**
 * The form's fields from the served values. GET serves the declared members a
 * stored row holds, fills in UTC for a missing time zone and fills in no hours
 * member. A missing member is an empty field the save then asks for.
 */
function formFrom(config: Partial<TenantSlaConfig>): FormState {
  return {
    greenHours: config.greenHours === undefined ? '' : String(config.greenHours),
    yellowHours: config.yellowHours === undefined ? '' : String(config.yellowHours),
    timezone: config.timezone ?? '',
  };
}

/** A whole number of hours within the spec's bounds, or null. */
function parseHours(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const hours = Number(trimmed);
  return hours >= SLA_HOURS_MIN && hours <= SLA_HOURS_MAX ? hours : null;
}

/** The test the server applies: Intl rejects an unknown time zone. */
export function isValidTimeZone(value: string): boolean {
  if (value.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** The IANA zones this browser knows, offered as suggestions. */
function supportedTimeZones(): string[] {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  return typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : [];
}

const HOURS_RANGE_MESSAGE = `Enter a whole number of hours from ${SLA_HOURS_MIN} to ${SLA_HOURS_MAX.toLocaleString('en-US')}.`;

/** The request body when every field passes, else the message per field. */
export function validateSlaForm(
  form: FormState,
): { ok: true; body: TenantSlaConfig } | { ok: false; errors: FormErrorState } {
  const errors: FormErrorState = {};
  const greenHours = parseHours(form.greenHours);
  const yellowHours = parseHours(form.yellowHours);
  const timezone = form.timezone.trim();

  if (greenHours === null) errors.greenHours = HOURS_RANGE_MESSAGE;
  if (yellowHours === null) {
    errors.yellowHours = HOURS_RANGE_MESSAGE;
  } else if (greenHours !== null && yellowHours <= greenHours) {
    errors.yellowHours = `Must be more than the Within SLA limit of ${greenHours} hours.`;
  }
  if (!isValidTimeZone(timezone)) {
    errors.timezone = 'Enter an IANA time zone, for example America/New_York.';
  }

  if (greenHours === null || yellowHours === null || Object.keys(errors).length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, body: { greenHours, yellowHours, timezone } };
}

/**
 * The label for served platform defaults: the institution has saved no
 * thresholds, and the queue bands against these until one is saved.
 */
function PlatformDefaultNotice() {
  return (
    <BodyText variant="ui" size="sm" color="secondary">
      These are the platform default; your institution has not set its own. Your Tasks queue
      labels against them until thresholds are saved.
    </BodyText>
  );
}

function ReadOnlyValues({ config }: { config: Partial<TenantSlaConfig> }) {
  const hours = (value: number | undefined) => (value === undefined ? 'Not saved' : `${value} hours`);
  const rows: Array<[string, string]> = [
    ['Within SLA under', hours(config.greenHours)],
    ['Over SLA from', hours(config.yellowHours)],
    ['Time zone', config.timezone ?? 'Not saved'],
  ];
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm font-sans">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-secondary">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SlaSettingsPage() {
  const { user } = useAuthStore();
  const canEdit =
    hasPlatformAdminRole(user?.roles) ||
    (user?.permissions ?? []).some((p) => p.toLowerCase() === 'settings:update');

  const slaQuery = useTenantSla();
  const update = useUpdateTenantSla();

  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<FormErrorState>({});
  const [saved, setSaved] = useState(false);
  const [timeZones] = useState(supportedTimeZones);

  // Seed the form from the server's values, and again after a save writes
  // the stored values into the query cache.
  useEffect(() => {
    if (slaQuery.data) setForm(formFrom(slaQuery.data));
  }, [slaQuery.data]);

  const baseline = slaQuery.data ? formFrom(slaQuery.data) : null;
  const dirty =
    form !== null &&
    baseline !== null &&
    (form.greenHours !== baseline.greenHours ||
      form.yellowHours !== baseline.yellowHours ||
      form.timezone !== baseline.timezone);

  const setField = (field: keyof FormState) => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setForm((current) => (current ? { ...current, [field]: value } : current));
    setErrors((current) => ({ ...current, [field]: undefined }));
    setSaved(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    setSaved(false);
    update.reset();
    const result = validateSlaForm(form);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    update.mutate(result.body, { onSuccess: () => setSaved(true) });
  };

  const handleReset = () => {
    if (baseline) setForm(baseline);
    setErrors({});
    setSaved(false);
    update.reset();
  };

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant heading. */}
        <p className="text-sm font-sans text-tertiary leading-normal max-w-3xl">
          The age thresholds your Tasks queue labels against. A task younger than the first
          threshold reads Within SLA, a task between the two reads Approaching, and a task at or
          past the second reads Over SLA. Ages count elapsed hours from when the task was created.
          A change applies to the queue the next time it loads.
        </p>
      </div>

      {slaQuery.isLoading ? (
        <div className="flex items-center gap-2 text-secondary">
          <Loader2 className="w-4 h-4 animate-spin" />
          <BodyText variant="ui" size="sm">
            Loading your SLA settings...
          </BodyText>
        </div>
      ) : slaQuery.error ? (
        <Alert type="error" title="Could not load SLA settings">
          <BodyText variant="ui" size="sm">
            {slaQuery.error.message}
          </BodyText>
        </Alert>
      ) : slaQuery.data && form ? (
        <Card className="p-6 max-w-2xl">
          <div className="space-y-4">
            {/* Under AppHeader's h1, so an h2 styled as a card title. */}
            <Heading level={2} as={3} size="card">
              Task age thresholds
            </Heading>

            {slaQuery.data.platformDefault && <PlatformDefaultNotice />}

            {!canEdit ? (
              <>
                <ReadOnlyValues config={slaQuery.data} />
                <BodyText variant="ui" size="sm" color="secondary">
                  Your role can view these thresholds. Changing them needs the settings update
                  permission, which your institution&apos;s administrator holds.
                </BodyText>
              </>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-4">
                {update.error && (
                  <Alert type="error" role="alert" title="Aaim refused this change">
                    <BodyText variant="ui" size="sm">
                      {update.error.message}
                    </BodyText>
                  </Alert>
                )}
                {/* A persistent live region, so the success notice is announced
                    when it appears (Alert's role guidance). */}
                <div role="status">
                  {saved && !dirty && (
                    <Alert type="success">
                      <BodyText variant="ui" size="sm">
                        Saved. Your Tasks queue now labels tasks against these thresholds.
                      </BodyText>
                    </Alert>
                  )}
                </div>

                <Input
                  id="sla-green-hours"
                  name="greenHours"
                  label="Within SLA under (hours)"
                  type="number"
                  inputMode="numeric"
                  min={SLA_HOURS_MIN}
                  max={SLA_HOURS_MAX}
                  step={1}
                  value={form.greenHours}
                  onChange={setField('greenHours')}
                  error={errors.greenHours}
                  helperText="A task younger than this reads Within SLA."
                  required
                />
                <Input
                  id="sla-yellow-hours"
                  name="yellowHours"
                  label="Over SLA from (hours)"
                  type="number"
                  inputMode="numeric"
                  min={SLA_HOURS_MIN}
                  max={SLA_HOURS_MAX}
                  step={1}
                  value={form.yellowHours}
                  onChange={setField('yellowHours')}
                  error={errors.yellowHours}
                  helperText="A task this old or older reads Over SLA. Between the two thresholds a task reads Approaching."
                  required
                />
                <Input
                  id="sla-timezone"
                  name="timezone"
                  label="Time zone"
                  value={form.timezone}
                  onChange={setField('timezone')}
                  error={errors.timezone}
                  helperText="An IANA time zone, for example America/New_York. It is saved with the thresholds; the age labels count elapsed hours and do not use it."
                  list={timeZones.length > 0 ? TIME_ZONE_LIST_ID : undefined}
                  autoComplete="off"
                  required
                />
                {timeZones.length > 0 && (
                  <datalist id={TIME_ZONE_LIST_ID}>
                    {timeZones.map((zone) => (
                      <option key={zone} value={zone} />
                    ))}
                  </datalist>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="small"
                    disabled={!dirty || update.isPending}
                  >
                    {update.isPending ? 'Saving...' : 'Save thresholds'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    disabled={!dirty || update.isPending}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </div>
              </form>
            )}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
